import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Radio, Video, Ticket, Sparkles, Copy, Check, Monitor } from 'lucide-react';
import {
  gameshowApi,
  pointsNow,
  setEmbedToken,
  GameShowApiError,
  type StateResponse,
  type StateCurrentQuestion,
  type StateCurrentMatch,
  type MatchSide,
} from '@/services/gameshowApi';

// ── /show/:showId ─────────────────────────────────────────────────────
// Phone-first audience watch page. Persistent stream band (Mux placeholder)
// at the top; the game panel below cycles through lobby → question →
// reveal → leaderboard → winner, driven entirely by GET /state (2s poll).

const PAGE_BG = 'radial-gradient(120% 70% at 50% -6%, #2a1c3c 0%, #16110f 52%, #0b0908 100%)';
const GOLD = '#ffd600';
const PINK = '#f286c9';
const GREEN = '#8cc63f';
const REC = '#ff4d6a';
const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

// Normalize openedAt (ms number, numeric string, or ISO) → ms epoch.
function toMs(v: number | string): number {
  if (typeof v === 'number') return v;
  const n = Number(v);
  if (!Number.isNaN(n) && String(v).trim() !== '') return n;
  return new Date(v).getTime();
}

const kicker: React.CSSProperties = {
  fontSize: 10,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  fontWeight: 600,
};

const GameShowAudiencePage: React.FC = () => {
  const { showId } = useParams<{ showId: string }>();
  // Embed mode: rendered inside casting's Game attendee page iframe at
  // /show-embed/:showId — no stream band (the parent page owns the player)
  // and auth arrives from the parent via postMessage instead of localStorage.
  const embed = window.location.pathname.startsWith('/show-embed/');
  const [embedAuthed, setEmbedAuthed] = useState(!embed);

  useEffect(() => {
    if (!embed) return;
    // Only accept auth from parents we trust: any heyjoe.io origin over https
    // (casting prod + casting dev/staging attendee pages) or our own origin
    // (standalone testing). Casting targets its postMessage at post.heyjoe.io;
    // this is the receiving half of that contract.
    const trustedParent = (origin: string) =>
      origin === window.location.origin || /^https:\/\/([a-z0-9-]+\.)*heyjoe\.io$/.test(origin);
    const onMsg = (e: MessageEvent) => {
      if (!trustedParent(e.origin)) return;
      const d = e.data;
      if (d && d.type === 'gameshow-auth' && typeof d.token === 'string' && d.token) {
        setEmbedToken(d.token);
        setEmbedAuthed(true);
      }
    };
    window.addEventListener('message', onMsg);
    // Announce readiness so the parent knows when to send the token; also
    // fall back to our own localStorage token for standalone testing.
    try {
      const own = localStorage.getItem('authToken') || localStorage.getItem('token');
      if (own) { setEmbedToken(own); setEmbedAuthed(true); }
    } catch { /* partitioned storage — wait for postMessage */ }
    window.parent?.postMessage({ type: 'gameshow-embed-ready', showId }, '*');
    return () => window.removeEventListener('message', onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embed]);

  const [st, setSt] = useState<StateResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The show is ticket-gated and this viewer isn't registered. gateProductId
  // (from the 403 body) links to the product page to buy in.
  const [registrationRequired, setRegistrationRequired] = useState(false);
  const [gateProductId, setGateProductId] = useState<string | null>(null);
  const [, setTick] = useState(0); // drives the live clock re-render

  // Server clock offset (serverNow - Date.now()), recomputed each poll.
  const offsetRef = useRef(0);
  // Points the backend actually awarded, keyed by question id (from POST /answer).
  const earnedRef = useRef<Record<string, number>>({});
  // Optimistic local pick so the button state flips instantly on tap.
  const [pending, setPending] = useState<{ qid: string; idx: number } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Come On Down: stage opt-in toggle.
  const [optBusy, setOptBusy] = useState(false);
  // Video Battle: optimistic local vote so the button flips instantly on tap.
  const [pendingVote, setPendingVote] = useState<{ mid: string; side: MatchSide } | null>(null);
  const [voting, setVoting] = useState(false);

  const poll = useCallback(async () => {
    if (!showId || !embedAuthed) return;
    try {
      const next = await gameshowApi.state(showId);
      // serverNow arrives as an ISO string (Mongo Date serialization) — it
      // must go through toMs like openedAt does, or the offset is NaN and the
      // whole decay meter shows "NaN pts" (caught in the first live smoke test).
      offsetRef.current = next.currentQuestion
        ? toMs(next.currentQuestion.serverNow) - Date.now()
        : 0;
      setSt(next);
      setError(null);
      setRegistrationRequired(false);
    } catch (e) {
      // Ticket gate: surface a register-to-play state (not a generic error).
      if (e instanceof GameShowApiError && e.code === 'REGISTRATION_REQUIRED') {
        setRegistrationRequired(true);
        const pid = e.body?.productId;
        setGateProductId(typeof pid === 'string' ? pid : null);
        setError(null);
        return;
      }
      setError(e instanceof Error ? e.message : 'Lost connection to the show.');
    }
    // embedAuthed MUST be a dep: it flips true when the parent frame delivers
    // the auth token, and the interval needs the recreated closure — otherwise
    // the embed polls with the stale pre-auth early-return forever.
  }, [showId, embedAuthed]);

  useEffect(() => {
    poll();
    const id = setInterval(poll, 2000);
    return () => clearInterval(id);
  }, [poll]);

  // Local 100ms ticker so the decay meter + countdown animate between polls.
  useEffect(() => {
    const open = st?.currentQuestion?.state === 'open';
    if (!open) return;
    const id = setInterval(() => setTick((n) => n + 1), 100);
    return () => clearInterval(id);
  }, [st?.currentQuestion?.state]);

  // Clear the optimistic pick when the active question changes.
  const cqId = st?.currentQuestion?.id ?? null;
  useEffect(() => {
    setPending(null);
  }, [cqId]);

  // Clear the optimistic vote when the active matchup changes.
  const cmId = st?.currentMatch?.id ?? null;
  useEffect(() => {
    setPendingVote(null);
  }, [cmId]);

  const cq = st?.currentQuestion ?? null;
  const myAnswer =
    cq && pending && pending.qid === cq.id ? pending.idx : cq?.myAnswer ?? null;
  const answered = myAnswer != null;

  const submitAnswer = async (idx: number) => {
    if (!showId || !cq || cq.state !== 'open' || answered || submitting) return;
    setPending({ qid: cq.id, idx });
    setSubmitting(true);
    try {
      const res = await gameshowApi.answer(showId, cq.id, idx);
      if (typeof res.pointsEarned === 'number') earnedRef.current[cq.id] = res.pointsEarned;
      poll();
    } catch {
      setPending(null); // let them retry
    } finally {
      setSubmitting(false);
    }
  };

  const cm = st?.currentMatch ?? null;
  const myVote = cm && pendingVote && pendingVote.mid === cm.id ? pendingVote.side : cm?.myVote ?? null;

  const submitVote = async (side: MatchSide) => {
    if (!showId || !cm || cm.state !== 'open' || myVote != null || voting) return;
    setPendingVote({ mid: cm.id, side });
    setVoting(true);
    try {
      await gameshowApi.voteMatch(showId, cm.id, side);
      poll();
    } catch {
      setPendingVote(null); // let them retry
    } finally {
      setVoting(false);
    }
  };

  const toggleOptIn = async () => {
    if (!showId || optBusy) return;
    const currentlyIn = !!st?.myOptIn;
    setOptBusy(true);
    // Optimistic flip so the button responds instantly; poll() resyncs the count.
    setSt((s) => (s ? { ...s, myOptIn: !currentlyIn } : s));
    try {
      if (currentlyIn) await gameshowApi.optOut(showId);
      else await gameshowApi.optIn(showId);
      await poll();
    } catch {
      await poll(); // resync on failure
    } finally {
      setOptBusy(false);
    }
  };

  // ── live clock ──
  const now = Date.now() + offsetRef.current;
  const elapsedMs = cq ? Math.max(0, now - toMs(cq.openedAt)) : 0;
  const elapsedSec = elapsedMs / 1000;
  const ptsNow = cq ? pointsNow(elapsedMs, cq.points, cq.decaySeconds) : 0;
  const meterPct = cq && cq.points ? Math.max(0, Math.min(100, (ptsNow / cq.points) * 100)) : 0;
  const remaining = cq ? Math.max(0, cq.lockSeconds - elapsedSec) : 0;

  const showName = st?.title || 'Game Show';

  // ── phase derivation ──
  const status = st?.status;
  // A battle show may interleave questions and matches. Panel ownership: an OPEN
  // question or match always wins; if neither is open, the most recently opened
  // one stays on screen (so a reveal lingers). Quiz shows never have cm.
  const battleOwns = (() => {
    if (!cm) return false;
    if (cm.state === 'open') return true;
    if (cq?.state === 'open') return false;
    if (!cq) return true;
    return toMs(cm.openedAt) >= toMs(cq.openedAt);
  })();

  const isBattle = status === 'live' && battleOwns;
  const isLobby = status === 'lobby' && !cq && !cm;
  const isOpen = status === 'live' && !battleOwns && cq?.state === 'open';
  const isLocked = status === 'live' && !battleOwns && cq?.state === 'locked';
  const isReveal = status === 'live' && !battleOwns && cq?.state === 'revealed';
  const isBoard = status === 'live' && !cq && !battleOwns;
  const isEnded = status === 'ended';

  const battleRoundLabel =
    cm?.state === 'open' ? 'Battle · vote now' : cm?.state === 'locked' ? 'Votes in' : 'Battle · reveal';
  const roundLabel = isEnded
    ? 'Final'
    : isBattle
    ? battleRoundLabel
    : isOpen
    ? 'Question · live'
    : isLocked
    ? 'Locked'
    : isReveal
    ? 'Reveal'
    : isBoard
    ? 'Between rounds'
    : 'Lobby';

  const videoCaption = isEnded
    ? "Crowning tonight's champion"
    : isBattle
    ? cm?.state === 'open'
      ? 'Head-to-head — cast your vote'
      : cm?.state === 'locked'
      ? 'Voting is closed'
      : 'Host reveals the winner'
    : isOpen
    ? cq?.delivery === 'host_read'
      ? 'Host is reading the question…'
      : 'Question is live'
    : isLocked
    ? 'Answers are in'
    : isReveal
    ? 'Host reacts to the answer'
    : isBoard
    ? 'Between rounds'
    : 'Pre-show · warming up the room';

  // COME ON DOWN takeover — highest priority, above every phase except ended.
  // When the host draws this viewer, their phone flips to a full-screen call.
  if (st?.myContestant && status !== 'ended') {
    return <ComeOnDownTakeover showName={showName} showId={showId!} />;
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: PAGE_BG,
        color: 'var(--hj-ink)',
        fontFamily: 'var(--hj-font-ui)',
        display: 'flex',
        justifyContent: 'center',
        padding: '18px 14px 40px',
      }}
    >
      <div style={{ width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', gap: 15 }}>
        {/* top row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ ...kicker, color: GOLD }}>{showName}</span>
          <span
            style={{
              ...kicker,
              color: 'rgba(255,255,255,.7)',
              border: '1px solid rgba(255,255,255,.18)',
              padding: '4px 9px',
              borderRadius: 999,
            }}
          >
            {roundLabel}
          </span>
        </div>

        {/* PERSISTENT STREAM BAND (Mux placeholder) — hidden in embed mode:
            casting's Game attendee page owns the real player above us. */}
        {!embed && (
        <div
          style={{
            position: 'relative',
            borderRadius: 16,
            overflow: 'hidden',
            aspectRatio: '16 / 9',
            background: 'repeating-linear-gradient(135deg, #1d1528 0 11px, #191322 11px 22px)',
            border: '1px solid rgba(255,255,255,.09)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {status === 'live' && (
            <div
              style={{
                position: 'absolute',
                top: 9,
                left: 9,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: 'rgba(255,77,106,.92)',
                color: '#fff',
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                padding: '4px 8px',
                borderRadius: 6,
              }}
            >
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#fff' }} />
              Live
            </div>
          )}
          <div
            style={{
              position: 'absolute',
              top: 9,
              right: 9,
              fontSize: 9,
              color: 'rgba(255,255,255,.7)',
              background: 'rgba(0,0,0,.45)',
              padding: '4px 8px',
              borderRadius: 6,
            }}
          >
            ~5s delay
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, opacity: 0.55 }}>
            <Video size={15} color="rgba(255,255,255,.6)" />
            <span
              style={{
                fontFamily: 'var(--hj-font-mono)',
                fontSize: 10,
                letterSpacing: '0.08em',
                color: 'rgba(255,255,255,.55)',
                textTransform: 'uppercase',
              }}
            >
              mux live · host cam
            </span>
          </div>
          <div
            style={{
              position: 'absolute',
              bottom: 9,
              left: 11,
              right: 11,
              fontSize: 12,
              fontWeight: 600,
              color: '#fff',
              textShadow: '0 1px 5px rgba(0,0,0,.7)',
            }}
          >
            {videoCaption}
          </div>
        </div>
        )}

        {/* GAME PANEL */}
        {registrationRequired && !st && (
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 15,
              textAlign: 'center',
              padding: '40px 14px',
            }}
          >
            <span
              style={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255,214,0,.12)',
                border: '1px solid rgba(255,214,0,.4)',
              }}
            >
              <Ticket size={26} color={GOLD} />
            </span>
            <div style={{ ...kicker, letterSpacing: '0.2em', color: GOLD }}>Registered players only</div>
            <h2 className="font-hj-display" style={{ fontSize: 27, margin: 0, color: '#fff', lineHeight: 1.1 }}>
              This show is for registered players
            </h2>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,.72)', maxWidth: 320, lineHeight: 1.45, margin: 0 }}>
              Grab your ticket to join the game and play along on your phone.
            </p>
            {gateProductId && (
              <Link
                to={`/course/${gateProductId}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  marginTop: 4,
                  padding: '12px 22px',
                  borderRadius: 999,
                  background: GOLD,
                  color: '#150e20',
                  fontSize: 14,
                  fontWeight: 700,
                  textDecoration: 'none',
                }}
              >
                <Ticket size={16} /> Get your ticket
              </Link>
            )}
          </div>
        )}

        {error && !st && (
          <div style={{ textAlign: 'center', color: '#ff9db3', fontSize: 14, padding: '24px 0' }}>{error}</div>
        )}

        {!st && !error && !registrationRequired && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              color: 'rgba(255,255,255,.6)',
              padding: '40px 0',
            }}
          >
            <Loader2 size={18} className="animate-spin" /> Joining the show…
          </div>
        )}

        {isLobby && (
          <>
            <Lobby showName={showName} playing={st!.answeredCount} />
            <OptInCard optInCount={st!.optInCount} myOptIn={st!.myOptIn} busy={optBusy} onToggle={toggleOptIn} />
          </>
        )}
        {isBattle && cm && (
          <>
            <BattleView cm={cm} myVote={myVote} voting={voting} onVote={submitVote} />
            {cm.state === 'revealed' && st?.sponsor && <SponsorLine name={st.sponsor.name} />}
          </>
        )}
        {isOpen && cq && (
          <OpenQuestion
            cq={cq}
            ptsNow={ptsNow}
            meterPct={meterPct}
            remaining={remaining}
            elapsedSec={elapsedSec}
            myAnswer={myAnswer}
            answered={answered}
            onPick={submitAnswer}
          />
        )}
        {isLocked && cq && <Locked cq={cq} myAnswer={myAnswer} />}
        {isReveal && cq && (
          <>
            <Reveal cq={cq} myAnswer={myAnswer} earned={earnedRef.current[cq.id]} />
            {st?.sponsor && <SponsorLine name={st.sponsor.name} />}
          </>
        )}
        {isBoard && st && (
          <>
            <BoardInterstitial st={st} />
            <OptInCard optInCount={st.optInCount} myOptIn={st.myOptIn} busy={optBusy} onToggle={toggleOptIn} />
          </>
        )}
        {isEnded && st && (
          <>
            <Winner st={st} />
            {st.sponsor && <SponsorLine name={st.sponsor.name} />}
          </>
        )}
      </div>
    </div>
  );
};

// ── LOBBY ─────────────────────────────────────────────────────────────
const Lobby: React.FC<{ showName: string; playing: number }> = ({ showName, playing }) => {
  const rules = [
    'Phones out — every question hits your screen',
    'The host reads each one aloud, so listen up',
    'Fastest correct answer wins the most points',
  ];
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        textAlign: 'center',
        padding: '10px 0',
      }}
    >
      <div style={{ ...kicker, letterSpacing: '0.28em', color: 'rgba(255,255,255,.55)' }}>
        Lobby · you're in the room
      </div>
      <h1
        className="font-hj-display"
        style={{
          fontSize: 38,
          margin: 0,
          textTransform: 'uppercase',
          color: GOLD,
          letterSpacing: '-0.01em',
        }}
      >
        {showName}
      </h1>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 15, fontWeight: 600 }}>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: GREEN }} />
        {playing > 0 ? `${playing} players here` : 'Waiting for players…'}
      </div>
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 9,
          textAlign: 'left',
          marginTop: 4,
        }}
      >
        <div style={{ ...kicker, color: 'rgba(255,255,255,.5)' }}>House rules</div>
        {rules.map((r) => (
          <div
            key={r}
            style={{ display: 'flex', gap: 11, alignItems: 'flex-start', fontSize: 14, lineHeight: 1.35, color: 'var(--hj-ink-2)' }}
          >
            <span style={{ color: GOLD, fontWeight: 700 }}>—</span>
            <span>{r}</span>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,.5)', fontSize: 12 }}>
        <Radio size={14} /> Waiting for the host to start…
      </div>
    </div>
  );
};

// ── OPEN QUESTION ─────────────────────────────────────────────────────
const OpenQuestion: React.FC<{
  cq: StateCurrentQuestion;
  ptsNow: number;
  meterPct: number;
  remaining: number;
  elapsedSec: number;
  myAnswer: number | null;
  answered: boolean;
  onPick: (idx: number) => void;
}> = ({ cq, ptsNow, meterPct, remaining, elapsedSec, myAnswer, answered, onPick }) => {
  const hostRead = cq.delivery === 'host_read';
  const ptsColor = meterPct > 66 ? GOLD : meterPct > 33 ? '#ff9d3f' : REC;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 13 }}>
      {hostRead ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            background: 'var(--hj-pink-tint)',
            border: '1px solid rgba(242,134,201,.4)',
            borderRadius: 12,
            padding: '10px 12px',
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: PINK, flexShrink: 0 }} />
          <span style={{ fontSize: 12.5, lineHeight: 1.3, color: 'var(--hj-ink)', fontWeight: 600 }}>
            Listen to the host — the question is read aloud, nothing on screen to search.
          </span>
        </div>
      ) : (
        <div style={{ fontSize: 19, fontWeight: 700, lineHeight: 1.28, color: 'var(--hj-ink)' }}>
          {cq.text}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <span style={{ ...kicker, color: 'rgba(255,255,255,.5)' }}>Points if you answer now</span>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,.55)' }}>{remaining.toFixed(1)}s left</span>
      </div>
      <div
        className="font-hj-display tabular"
        style={{ fontSize: 54, lineHeight: 0.9, color: ptsColor, fontWeight: 800 }}
      >
        {ptsNow}
        <span style={{ fontSize: 18, color: 'rgba(255,255,255,.4)', fontWeight: 600, fontFamily: 'var(--hj-font-ui)' }}>
          {' '}
          pts
        </span>
      </div>
      <div style={{ width: '100%', height: 13, borderRadius: 999, background: 'rgba(255,255,255,.08)', overflow: 'hidden' }}>
        <div
          style={{
            height: '100%',
            width: `${meterPct}%`,
            borderRadius: 999,
            background: 'linear-gradient(90deg,#f286c9,#ffd600)',
            transition: 'width .12s linear',
          }}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
        {cq.options.map((opt, i) => {
          const picked = myAnswer === i;
          const dim = answered && !picked;
          return (
            <button
              key={i}
              onClick={() => onPick(i)}
              disabled={answered}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 13,
                textAlign: 'left',
                cursor: answered ? 'default' : 'pointer',
                width: '100%',
                border: `2px solid ${picked ? 'rgba(255,214,0,.9)' : 'rgba(255,255,255,.14)'}`,
                background: picked ? 'rgba(255,214,0,.12)' : 'rgba(255,255,255,.05)',
                opacity: dim ? 0.4 : 1,
                borderRadius: 16,
                padding: '15px',
                transition: 'all .16s',
              }}
            >
              <span
                style={{
                  flexShrink: 0,
                  width: 30,
                  height: 30,
                  borderRadius: 9,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 14,
                  background: picked ? GOLD : 'rgba(255,255,255,.1)',
                  color: picked ? '#150e20' : 'rgba(255,255,255,.85)',
                }}
              >
                {LETTERS[i]}
              </span>
              <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: 'var(--hj-ink)' }}>{opt}</span>
              {picked && (
                <span style={{ ...kicker, color: GOLD }}>locked</span>
              )}
            </button>
          );
        })}
      </div>

      <div style={{ marginTop: 'auto', textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,.5)', paddingTop: 6 }}>
        {answered ? 'Locked in. Waiting for the host to reveal…' : 'Tap your answer — fast.'}
      </div>
    </div>
  );
};

// ── LOCKED ────────────────────────────────────────────────────────────
const Locked: React.FC<{ cq: StateCurrentQuestion; myAnswer: number | null }> = ({ cq, myAnswer }) => (
  <div
    style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 14,
      textAlign: 'center',
      padding: '30px 0',
    }}
  >
    <div style={{ ...kicker, letterSpacing: '0.24em', color: 'rgba(255,255,255,.55)' }}>Answers in</div>
    <div className="font-hj-display" style={{ fontSize: 40, color: GOLD }}>
      Locked
    </div>
    <div style={{ fontSize: 13.5, color: 'var(--hj-ink-2)', maxWidth: 280, lineHeight: 1.4 }}>
      {myAnswer != null
        ? `You answered ${LETTERS[myAnswer]}. Hang tight — the host is about to reveal.`
        : "No answer this time. Watch the reveal on stream."}
    </div>
  </div>
);

// ── REVEAL ────────────────────────────────────────────────────────────
const Reveal: React.FC<{ cq: StateCurrentQuestion; myAnswer: number | null; earned?: number }> = ({
  cq,
  myAnswer,
  earned,
}) => {
  const correct = cq.correctOption;
  const isVote = correct == null;
  const gotIt = correct != null && myAnswer === correct;
  const tally = cq.tally || [];
  const total = tally.reduce((a, b) => a + b, 0);

  const resultBig = isVote
    ? 'Votes in'
    : gotIt
    ? earned != null
      ? `+${earned}`
      : 'You got it'
    : myAnswer == null
    ? 'No answer'
    : 'Missed it';
  const resultKicker = isVote
    ? 'The room has spoken'
    : gotIt
    ? 'You got it'
    : myAnswer == null
    ? "You didn't answer"
    : 'Not this time';
  const good = isVote || gotIt;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 13 }}>
      <div
        style={{
          borderRadius: 18,
          padding: 18,
          textAlign: 'center',
          background: good ? 'rgba(140,198,63,.12)' : 'rgba(255,77,106,.1)',
          border: `1px solid ${good ? 'rgba(140,198,63,.4)' : 'rgba(255,77,106,.35)'}`,
        }}
      >
        <div style={{ ...kicker, letterSpacing: '0.2em', color: 'rgba(255,255,255,.6)' }}>{resultKicker}</div>
        <div
          className="font-hj-display"
          style={{ fontSize: 50, lineHeight: 1, color: good ? GREEN : PINK }}
        >
          {resultBig}
        </div>
      </div>

      {cq.text && (
        <div
          style={{
            fontSize: 13.5,
            lineHeight: 1.4,
            color: 'var(--hj-ink-2)',
            background: 'rgba(255,255,255,.04)',
            borderRadius: 14,
            padding: '13px 14px',
          }}
        >
          <span style={{ ...kicker, color: 'rgba(255,255,255,.5)', display: 'block', marginBottom: 5 }}>
            The question was
          </span>
          {cq.text}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {cq.options.map((opt, i) => {
          const isCorrect = correct === i;
          const isMine = myAnswer === i;
          const pct = total ? Math.round((tally[i] || 0) / total * 100) : 0;
          let border = 'rgba(255,255,255,.08)';
          let bg = 'rgba(255,255,255,.02)';
          let barColor = 'rgba(255,255,255,.06)';
          let fg: string = 'rgba(255,255,255,.6)';
          let letterBg = 'rgba(255,255,255,.06)';
          let letterFg = 'rgba(255,255,255,.5)';
          let tag = '';
          let tagColor = GOLD;
          if (isCorrect) {
            border = GREEN;
            bg = 'rgba(140,198,63,.1)';
            barColor = 'rgba(140,198,63,.22)';
            fg = 'var(--hj-ink)';
            letterBg = GREEN;
            letterFg = '#08110c';
            tag = isMine ? '✓ you' : '✓';
            tagColor = GREEN;
          } else if (isMine) {
            border = PINK;
            bg = 'rgba(242,134,201,.08)';
            barColor = 'rgba(242,134,201,.2)';
            fg = 'var(--hj-ink)';
            tag = 'your pick';
            tagColor = PINK;
          }
          return (
            <div
              key={i}
              style={{
                position: 'relative',
                overflow: 'hidden',
                border: `2px solid ${border}`,
                background: bg,
                borderRadius: 14,
                padding: '13px 14px',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: `${pct}%`,
                  background: barColor,
                  transition: 'width .6s cubic-bezier(.2,.7,.2,1)',
                }}
              />
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12 }}>
                <span
                  style={{
                    flexShrink: 0,
                    width: 26,
                    height: 26,
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: 13,
                    background: letterBg,
                    color: letterFg,
                  }}
                >
                  {LETTERS[i]}
                </span>
                <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: fg }}>{opt}</span>
                {tag && <span style={{ ...kicker, fontSize: 9, color: tagColor }}>{tag}</span>}
                <span className="font-hj-display tabular" style={{ fontSize: 18, color: fg }}>
                  {pct}%
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 'auto', textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,.5)', paddingTop: 6 }}>
        Host is reacting on stream…
      </div>
    </div>
  );
};

// ── VIDEO BATTLE ──────────────────────────────────────────────────────
// Two tapes head-to-head. Tap to play each (muted autoplay is OFF); vote A/B
// while the match is open; on reveal the tally bars animate and the winner
// side is highlighted.
const BattleView: React.FC<{
  cm: StateCurrentMatch;
  myVote: MatchSide | null;
  voting: boolean;
  onVote: (side: MatchSide) => void;
}> = ({ cm, myVote, voting, onVote }) => {
  const open = cm.state === 'open';
  const showTally = cm.state === 'locked' || cm.state === 'revealed';
  const revealed = cm.state === 'revealed';
  const tally = cm.tally || { a: 0, b: 0 };
  const total = tally.a + tally.b;
  const pct = (side: MatchSide) => (total ? Math.round((tally[side] / total) * 100) : 0);
  // Winner is only declared on reveal; a tie has no winner.
  const winner: MatchSide | null = revealed && total > 0 && tally.a !== tally.b ? (tally.a > tally.b ? 'a' : 'b') : null;

  const answered = myVote != null;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 13 }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ ...kicker, letterSpacing: '0.2em', color: GOLD }}>
          Round {cm.round} · Battle
        </div>
        <div className="font-hj-display" style={{ fontSize: 26, color: '#fff', lineHeight: 1.05, marginTop: 2 }}>
          {open ? 'Who nailed it?' : revealed ? 'The room has spoken' : 'Votes are in'}
        </div>
      </div>

      {(['a', 'b'] as MatchSide[]).map((side) => {
        const tape = side === 'a' ? cm.tapeA : cm.tapeB;
        const picked = myVote === side;
        const isWinner = winner === side;
        const dim = answered && !picked && !showTally;
        const accent = side === 'a' ? GOLD : PINK;
        return (
          <div
            key={side}
            style={{
              borderRadius: 16,
              overflow: 'hidden',
              border: `2px solid ${isWinner ? GREEN : picked ? accent : 'rgba(255,255,255,.12)'}`,
              background: isWinner ? 'rgba(140,198,63,.08)' : 'rgba(255,255,255,.04)',
              opacity: dim ? 0.55 : 1,
              transition: 'all .2s',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ position: 'relative', background: '#000', aspectRatio: '16 / 9' }}>
              {tape ? (
                <video
                  src={tape.videoUrl}
                  playsInline
                  controls
                  preload="metadata"
                  style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'rgba(255,255,255,.4)',
                    fontSize: 12,
                  }}
                >
                  <Video size={16} />
                </div>
              )}
              <span
                style={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  ...kicker,
                  fontSize: 11,
                  color: '#150e20',
                  background: accent,
                  padding: '3px 9px',
                  borderRadius: 7,
                }}
              >
                {side === 'a' ? 'A' : 'B'}
              </span>
              {isWinner && (
                <span
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    ...kicker,
                    fontSize: 10,
                    color: '#08110c',
                    background: GREEN,
                    padding: '3px 9px',
                    borderRadius: 7,
                  }}
                >
                  Winner
                </span>
              )}
            </div>

            <div style={{ padding: '11px 13px', display: 'flex', flexDirection: 'column', gap: 9 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--hj-ink)', lineHeight: 1.2 }}>
                  {tape?.title || 'Untitled tape'}
                </div>
                <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.6)', marginTop: 2 }}>{tape?.userName || 'Unknown'}</div>
              </div>

              {open ? (
                <button
                  onClick={() => onVote(side)}
                  disabled={answered || voting}
                  style={{
                    width: '100%',
                    cursor: answered || voting ? 'default' : 'pointer',
                    border: picked ? `1px solid ${accent}` : 'none',
                    borderRadius: 12,
                    padding: 13,
                    fontFamily: 'var(--hj-font-ui)',
                    fontWeight: 700,
                    fontSize: 14.5,
                    color: picked ? accent : '#150e20',
                    background: picked ? 'rgba(255,255,255,.06)' : `linear-gradient(90deg, ${accent}, #ffe566)`,
                    opacity: answered && !picked ? 0.5 : 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  {voting && picked ? <Loader2 size={15} className="animate-spin" /> : picked ? <Check size={16} /> : null}
                  {picked ? 'Your vote' : `Vote ${side === 'a' ? 'A' : 'B'}`}
                </button>
              ) : showTally ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                    <span style={{ ...kicker, fontSize: 9, color: picked ? accent : 'rgba(255,255,255,.5)' }}>
                      {picked ? 'Your vote' : 'Votes'}
                    </span>
                    <span className="font-hj-display tabular" style={{ fontSize: 20, color: isWinner ? GREEN : 'var(--hj-ink)' }}>
                      {pct(side)}%
                    </span>
                  </div>
                  <div style={{ width: '100%', height: 10, borderRadius: 999, background: 'rgba(255,255,255,.08)', overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        width: `${pct(side)}%`,
                        borderRadius: 999,
                        background: isWinner ? GREEN : accent,
                        transition: 'width .6s cubic-bezier(.2,.7,.2,1)',
                      }}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        );
      })}

      <div style={{ marginTop: 'auto', textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,.5)', paddingTop: 6 }}>
        {open
          ? answered
            ? 'Vote locked in. Waiting for the host to reveal…'
            : 'Watch both, then tap your winner.'
          : revealed
          ? 'Host is reacting on stream…'
          : 'Voting closed — reveal coming up.'}
      </div>
    </div>
  );
};

// ── LEADERBOARD INTERSTITIAL ──────────────────────────────────────────
const BoardInterstitial: React.FC<{ st: StateResponse }> = ({ st }) => {
  const top = st.leaderboard.top.slice(0, 10);
  const me = st.leaderboard.me;
  const meInTop = me != null && top.some((r) => r.rank === me.rank);
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ textAlign: 'center', margin: '4px 0 2px' }}>
        <div style={{ ...kicker, letterSpacing: '0.2em', color: 'rgba(255,255,255,.5)' }}>Leaderboard</div>
        <div className="font-hj-display" style={{ fontSize: 28 }}>
          Who's in the lead
        </div>
      </div>
      {top.length === 0 && (
        <div style={{ textAlign: 'center', color: 'rgba(255,255,255,.55)', fontSize: 13, padding: '20px 0' }}>
          No scores yet — the first question sets the board.
        </div>
      )}
      {top.map((row, i) => (
        <div
          key={row.rank}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 13,
            padding: '12px 14px',
            borderRadius: 14,
            background: i === 0 ? 'rgba(255,214,0,.1)' : 'rgba(255,255,255,.04)',
            border: `1px solid ${i === 0 ? 'rgba(255,214,0,.4)' : 'rgba(255,255,255,.08)'}`,
          }}
        >
          <span
            className="font-hj-display tabular"
            style={{ fontSize: 20, width: 28, color: i === 0 ? GOLD : 'rgba(255,255,255,.7)' }}
          >
            {row.rank}
          </span>
          <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: 'var(--hj-ink)' }}>{row.userName}</span>
          <span className="font-hj-display tabular" style={{ fontSize: 18, color: GOLD }}>
            {row.totalPoints.toLocaleString()}
          </span>
        </div>
      ))}
      {me && !meInTop && (
        <div
          style={{
            marginTop: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 13,
            padding: 14,
            borderRadius: 14,
            background: 'linear-gradient(90deg, rgba(255,214,0,.16), rgba(242,134,201,.12))',
            border: '1.5px solid rgba(255,214,0,.5)',
          }}
        >
          <span className="font-hj-display tabular" style={{ fontSize: 20, width: 44, color: GOLD }}>
            #{me.rank}
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--hj-ink)' }}>You</div>
          </div>
          <span className="font-hj-display tabular" style={{ fontSize: 18, color: GOLD }}>
            {me.totalPoints.toLocaleString()}
          </span>
        </div>
      )}
      <div style={{ marginTop: 'auto', textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,.5)', paddingTop: 6 }}>
        Host is queuing the next round…
      </div>
    </div>
  );
};

// ── WINNER ────────────────────────────────────────────────────────────
const Winner: React.FC<{ st: StateResponse }> = ({ st }) => {
  const champ = st.leaderboard.top[0];
  const me = st.leaderboard.me;
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 18,
        textAlign: 'center',
        padding: '30px 0',
      }}
    >
      <div
        className="font-hj-display"
        style={{
          width: 78,
          height: 78,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 26,
          color: '#150e20',
          background: 'linear-gradient(160deg,#ffe566,#ffb800)',
          boxShadow: '0 0 40px -6px rgba(255,214,0,.7)',
        }}
      >
        1st
      </div>
      <div>
        <div style={{ ...kicker, letterSpacing: '0.28em', color: 'rgba(255,255,255,.55)' }}>Tonight's champion</div>
        <div className="font-hj-display" style={{ fontSize: 44, lineHeight: 1, color: GOLD, textTransform: 'uppercase' }}>
          {champ ? champ.userName : 'No winner'}
        </div>
        {champ && (
          <div style={{ fontSize: 14, color: 'var(--hj-ink-2)', marginTop: 4 }}>
            {champ.totalPoints.toLocaleString()} pts
          </div>
        )}
      </div>
      {me && (
        <div style={{ fontSize: 13.5, color: 'var(--hj-ink-2)' }}>
          You finished{' '}
          <b style={{ color: 'var(--hj-ink)' }}>#{me.rank}</b> with {me.totalPoints.toLocaleString()} pts — nice run.
        </div>
      )}
    </div>
  );
};

// ── SPONSOR PROVENANCE ────────────────────────────────────────────────
// Small mono-label shown in reveal + winner states. Cash prizes are
// sponsor-funded and the source is disclosed on-air ("Prize sponsored by X").
const SponsorLine: React.FC<{ name: string }> = ({ name }) => (
  <div
    style={{
      textAlign: 'center',
      fontFamily: 'var(--hj-font-mono)',
      fontSize: 11,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: GOLD,
      opacity: 0.85,
      paddingTop: 4,
    }}
  >
    Prize sponsored by {name}
  </div>
);

// ── OPT-IN CARD (Come On Down) ────────────────────────────────────────
// Shown in lobby + between-rounds. Toggles the viewer into the stage pool.
const OptInCard: React.FC<{ optInCount: number; myOptIn: boolean; busy: boolean; onToggle: () => void }> = ({
  optInCount,
  myOptIn,
  busy,
  onToggle,
}) => (
  <div
    style={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      borderRadius: 18,
      padding: 16,
      background: myOptIn ? 'rgba(140,198,63,.1)' : 'rgba(255,255,255,.05)',
      border: `1px solid ${myOptIn ? 'rgba(140,198,63,.4)' : 'rgba(255,255,255,.12)'}`,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
      <Sparkles size={16} color={GOLD} />
      <span style={{ ...kicker, letterSpacing: '0.16em', color: GOLD }}>Come on down</span>
    </div>
    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--hj-ink)', lineHeight: 1.25 }}>
      {myOptIn ? "You're in the stage pool" : 'Want a shot at the stage?'}
    </div>
    <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.6)', lineHeight: 1.4 }}>
      {myOptIn
        ? `${optInCount} ${optInCount === 1 ? 'player' : 'players'} in the pool · get drawn and you'll join on camera from a computer.`
        : "Opt in and you might get called down to play on camera for real stakes. On your phone you just play along."}
    </div>
    <button
      onClick={onToggle}
      disabled={busy}
      style={{
        marginTop: 2,
        width: '100%',
        cursor: busy ? 'default' : 'pointer',
        border: myOptIn ? '1px solid rgba(140,198,63,.5)' : 'none',
        borderRadius: 14,
        padding: 14,
        fontFamily: 'var(--hj-font-ui)',
        fontWeight: 700,
        fontSize: 14.5,
        color: myOptIn ? GREEN : '#150e20',
        background: myOptIn ? 'rgba(140,198,63,.1)' : 'linear-gradient(90deg,#f286c9,#ffd600)',
        opacity: busy ? 0.6 : 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
      }}
    >
      {busy ? <Loader2 size={15} className="animate-spin" /> : myOptIn ? <Check size={16} /> : <Sparkles size={16} />}
      {myOptIn ? "I'm camera-ready · tap to leave" : "I'm camera-ready"}
    </button>
  </div>
);

// ── COME ON DOWN TAKEOVER ─────────────────────────────────────────────
// Full-screen "you've been drawn" moment. Instructs the player to open the
// contestant stage on a desktop.
const ComeOnDownTakeover: React.FC<{ showName: string; showId: string }> = ({ showName, showId }) => {
  const stageUrl = `${window.location.origin}/show-stage/${showId}`;
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(stageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — the URL is shown for manual entry */
    }
  };
  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(120% 80% at 50% -10%, #ffd60022 0%, #2a1c3c 30%, #16110f 70%, #0b0908 100%)',
        color: 'var(--hj-ink)',
        fontFamily: 'var(--hj-font-ui)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '32px 20px',
        gap: 20,
      }}
    >
      <div style={{ ...kicker, letterSpacing: '0.3em', color: 'rgba(255,255,255,.6)' }}>{showName}</div>
      <Sparkles size={40} color={GOLD} />
      <h1
        className="font-hj-display"
        style={{
          fontSize: 'clamp(46px, 15vw, 84px)',
          lineHeight: 0.92,
          margin: 0,
          textTransform: 'uppercase',
          color: GOLD,
          textShadow: '0 0 44px rgba(255,214,0,.5)',
          letterSpacing: '-0.01em',
        }}
      >
        Come on down!
      </h1>
      <p style={{ fontSize: 16, color: 'var(--hj-ink)', maxWidth: 360, lineHeight: 1.45, margin: 0, fontWeight: 600 }}>
        You've been drawn — the host will bring you on stage.
      </p>

      <div
        style={{
          width: '100%',
          maxWidth: 420,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          borderRadius: 18,
          padding: 18,
          background: 'rgba(255,255,255,.05)',
          border: '1px solid rgba(255,255,255,.14)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, ...kicker, color: PINK }}>
          <Monitor size={16} /> Open this on a desktop to play
        </div>
        <div
          style={{
            fontFamily: 'var(--hj-font-mono)',
            fontSize: 15,
            color: '#fff',
            background: 'rgba(0,0,0,.35)',
            borderRadius: 12,
            padding: '13px 14px',
            wordBreak: 'break-all',
            lineHeight: 1.35,
          }}
        >
          {stageUrl}
        </div>
        <button
          onClick={copy}
          style={{
            width: '100%',
            cursor: 'pointer',
            border: 'none',
            borderRadius: 14,
            padding: 14,
            fontFamily: 'var(--hj-font-ui)',
            fontWeight: 700,
            fontSize: 15,
            color: '#150e20',
            background: 'linear-gradient(90deg,#ffe566,#ffd600)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
          }}
        >
          {copied ? <Check size={17} /> : <Copy size={17} />}
          {copied ? 'Copied!' : 'Copy stage link'}
        </button>
      </div>
      <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.5)', maxWidth: 340, lineHeight: 1.4 }}>
        Keep this phone nearby — you'll play the on-camera questions from the desktop page.
      </div>
    </div>
  );
};

export default GameShowAudiencePage;
